import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Loader,
  Stack,
  Text,
  Center,
  Alert,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import api from "../../../../services/api";
import {
  useMenuV2,
  readCategoryOrder,
  getItemCategories,
  getItemEnglishCategories,
} from "./hooks/useMenuV2";
import { MenuHeader } from "./MenuHeader";
import { PageRail } from "./PageRail";
import { ItemList } from "./ItemList";
import { MenuSettingsDrawer } from "./MenuSettingsDrawer";

export const MenuEditV2: React.FC = () => {
  const { id: menuId } = useParams<{ id: string }>();
  const {
    menu,
    isLoading,
    error,
    refetch,
    saveStatus,
    lastSavedAt,
    updateMenu,
    updateItem,
    deleteItem,
    createItem,
    renameCategory,
    reorderCategories,
    patchMenu,
  } = useMenuV2(menuId || "");

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Auto-select the first page once data loads
  useEffect(() => {
    if (!selectedPageId && menu?.menuPages?.length) {
      setSelectedPageId(menu.menuPages[0].id);
    }
  }, [menu, selectedPageId]);

  const selectedPage = useMemo(
    () => menu?.menuPages?.find((p) => p.id === selectedPageId) || null,
    [menu, selectedPageId]
  );

  const allCategories = useMemo(() => {
    if (!menu) return [];
    const order = readCategoryOrder(menu.categoryOrder);
    const seen = new Set<string>(order);
    (menu.menuPages || []).forEach((p) =>
      (p.menuItems || []).forEach((it) => {
        getItemCategories(it).forEach((c) => c && seen.add(c));
        getItemEnglishCategories(it).forEach((c) => c && seen.add(c));
      })
    );
    return Array.from(seen);
  }, [menu]);

  const handleCreatePage = async () => {
    if (!menuId) return;
    try {
      const res = await api.post(`/menu-pages`, { menuId });
      const created = (res.data as any).menuPage || res.data;
      patchMenu((m) => ({
        ...m,
        menuPages: [...(m.menuPages || []), created],
      }));
      setSelectedPageId(created.id);
    } catch (e: any) {
      notifications.show({
        color: "red",
        title: "Could not create page",
        message: e?.response?.data?.error || e?.message || "Unknown error",
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="60vh">
        <Loader />
      </Center>
    );
  }

  if (error || !menu) {
    return (
      <Box p="lg">
        <Alert color="red" title="Could not load menu">
          {(error as any)?.message || "Unknown error"}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      style={{
        height: "calc(100vh - 60px)", // header offset from AdminLayout
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MenuHeader
        menu={menu}
        saveStatus={saveStatus}
        lastSavedAt={lastSavedAt}
        onOpenSettings={() => setSettingsOpen(true)}
      />

      <Box style={{ flex: 1, display: "flex", overflow: "hidden", flexDirection: isMobile ? "column" : "row" }}>
        <PageRail
          menu={menu}
          selectedPageId={selectedPageId}
          onSelectPage={setSelectedPageId}
          onCreatePage={handleCreatePage}
          onRenameCategory={renameCategory}
          onReorderCategories={reorderCategories}
          mobile={isMobile}
        />

        <Box style={{ flex: 1, overflow: "hidden" }}>
          {selectedPage ? (
            <ItemList
              page={selectedPage}
              allCategories={allCategories}
              onUpdateItem={updateItem}
              onDeleteItem={deleteItem}
              onCreateItem={(data) => createItem(selectedPage.id, data).then(() => undefined)}
            />
          ) : (
            <Center h="100%">
              <Stack align="center" gap="xs">
                <Text c="dimmed" size="sm">
                  No page selected
                </Text>
                <Text c="dimmed" size="xs">
                  {isMobile ? "Add a page above to get started." : "Add a page from the left to get started."}
                </Text>
              </Stack>
            </Center>
          )}
        </Box>
      </Box>

      <MenuSettingsDrawer
        menu={menu}
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onUpdate={updateMenu}
        onRefetch={() => refetch()}
      />
    </Box>
  );
};

export default MenuEditV2;
